// @truco6/marcador: o placar do truco presencial (CLAUDE.md, Estrutura). Dois lados com nome, somas de +1, +3, +6, +9
// e +12, desfazer com o histórico da partida, avisos de mão de onze e mão de ferro, vitória em 12 e as partidas da
// noite. Só lógica: toda função devolve um placar novo e não muda o que recebeu.

export const ALVO = 12; // a partida vai até 12
export const ONZE = ALVO - 1;
export const SOMAS = [1, 3, 6, 9, 12] as const; // a mão, o truco, o seis, o nove e o doze
export type Soma = (typeof SOMAS)[number];
export type Lado = 0 | 1;
export const LADOS: readonly Lado[] = [0, 1];
export const NOMES_PADRAO: readonly [string, string] = ['Nós', 'Eles'];
export const NOME_MAX = 20; // caracteres

export interface Jogada {
  lado: Lado;
  soma: Soma;
  // O placar antes da jogada: desfazer volta exatamente a ele.
  antes: { pontos: [number, number]; vencedor: Lado | null; vitorias: [number, number] };
}

export interface Placar {
  versao: 1;
  nomes: [string, string];
  pontos: [number, number];
  vencedor: Lado | null; // a partida acabou: só "Nova partida" (ou desfazer) volta a somar
  vitorias: [number, number]; // partidas da noite
  historico: Jogada[]; // da partida em curso, da primeira para a última
}

export function novoPlacar(nomes: readonly [string, string] = NOMES_PADRAO): Placar {
  return { versao: 1, nomes: [nomes[0], nomes[1]], pontos: [0, 0], vencedor: null, vitorias: [0, 0], historico: [] };
}

export const ehSoma = (n: unknown): n is Soma => typeof n === 'number' && (SOMAS as readonly number[]).includes(n);
export const outroLado = (l: Lado): Lado => (l === 0 ? 1 : 0);

// Soma pontos a um lado. Ao passar de 12, fecha em 12 e marca a vitória (mais uma partida da noite para o lado).
export function somar(p: Placar, lado: Lado, soma: Soma): Placar {
  if (p.vencedor !== null || !ehSoma(soma)) return p;
  const pontos: [number, number] = [p.pontos[0], p.pontos[1]];
  pontos[lado] = Math.min(ALVO, pontos[lado] + soma);
  const venceu = pontos[lado] >= ALVO;
  const vitorias: [number, number] = [p.vitorias[0], p.vitorias[1]];
  if (venceu) vitorias[lado] += 1;
  const antes = { pontos: [p.pontos[0], p.pontos[1]] as [number, number], vencedor: p.vencedor, vitorias: [p.vitorias[0], p.vitorias[1]] as [number, number] };
  return { ...p, pontos, vencedor: venceu ? lado : null, vitorias, historico: [...p.historico, { lado, soma, antes }] };
}

// Desfaz a última soma da partida, inclusive a que deu a vitória.
export function desfazer(p: Placar): Placar {
  const ultima = p.historico[p.historico.length - 1];
  if (!ultima) return p;
  const { pontos, vencedor, vitorias } = ultima.antes;
  return { ...p, pontos: [pontos[0], pontos[1]], vencedor, vitorias: [vitorias[0], vitorias[1]], historico: p.historico.slice(0, -1) };
}

// Nova partida: placar zerado, histórico limpo e as vitórias da noite mantidas.
export function novaPartida(p: Placar): Placar {
  return { ...p, pontos: [0, 0], vencedor: null, historico: [] };
}

// Nova noite: zera também as vitórias. Os nomes ficam.
export function novaNoite(p: Placar): Placar {
  return novoPlacar(p.nomes);
}

// Nome de um lado: até 20 caracteres, sem espaço sobrando; vazio volta ao padrão (Nós ou Eles).
export function renomear(p: Placar, lado: Lado, nome: string): Placar {
  const limpo = [...nome.replace(/\s+/g, ' ').trim()].slice(0, NOME_MAX).join('');
  const nomes: [string, string] = [p.nomes[0], p.nomes[1]];
  nomes[lado] = limpo || NOMES_PADRAO[lado];
  return { ...p, nomes };
}

export type Aviso = { tipo: 'mao_de_onze'; lado: Lado } | { tipo: 'mao_de_ferro' } | { tipo: 'vitoria'; lado: Lado } | null;

// O aviso da próxima mão: mão de onze para o lado com 11, mão de ferro com os dois em 11, ou a vitória.
export function avisoDa(p: Placar): Aviso {
  if (p.vencedor !== null) return { tipo: 'vitoria', lado: p.vencedor };
  const [a, b] = p.pontos;
  if (a === ONZE && b === ONZE) return { tipo: 'mao_de_ferro' };
  if (a === ONZE) return { tipo: 'mao_de_onze', lado: 0 };
  if (b === ONZE) return { tipo: 'mao_de_onze', lado: 1 };
  return null;
}

// Quanto falta para cada lado chegar a 12.
export const faltam = (p: Placar, lado: Lado): number => ALVO - p.pontos[lado];

// ---------- guardar e recuperar ----------

export function paraTexto(p: Placar): string {
  return JSON.stringify(p);
}

const ehLado = (x: unknown): x is Lado => x === 0 || x === 1;
const ehPar = (x: unknown, max: number): x is [number, number] =>
  Array.isArray(x) && x.length === 2 && x.every((n) => Number.isInteger(n) && n >= 0 && n <= max);

// Lê um placar guardado. Devolve null se o texto não for um placar válido (aparelho com dado velho ou alterado).
export function deTexto(texto: string | null | undefined): Placar | null {
  if (!texto) return null;
  let x: unknown;
  try { x = JSON.parse(texto); } catch { return null; }
  if (!x || typeof x !== 'object') return null;
  const o = x as Record<string, unknown>;
  if (o.versao !== 1 || !Array.isArray(o.nomes) || o.nomes.length !== 2 || !o.nomes.every((n) => typeof n === 'string')) return null;
  if (!ehPar(o.pontos, ALVO) || !ehPar(o.vitorias, 10_000) || !(o.vencedor === null || ehLado(o.vencedor)) || !Array.isArray(o.historico)) return null;
  for (const j of o.historico as unknown[]) {
    const h = j as Record<string, unknown> | null;
    if (!h || !ehLado(h.lado) || !ehSoma(h.soma) || !h.antes || typeof h.antes !== 'object') return null;
    const a = h.antes as Record<string, unknown>;
    if (!ehPar(a.pontos, ALVO) || !ehPar(a.vitorias, 10_000) || !(a.vencedor === null || ehLado(a.vencedor))) return null;
  }
  let p = novoPlacar(o.nomes as [string, string]);
  p = renomear(renomear(p, 0, p.nomes[0]), 1, p.nomes[1]);
  return { ...p, pontos: o.pontos as [number, number], vencedor: o.vencedor as Lado | null, vitorias: o.vitorias as [number, number], historico: o.historico as Jogada[] };
}
